import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import { FulfillmentGroupStatus } from "@redotech/redo-model/fulfillments/fulfillment-group-status";
import { ArrayStringFilterOperator } from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import {
  DateFilterOperator,
  KnownDateFilterTimeFrame,
} from "@redotech/redo-model/views/advanced-filters/date-filter";
import { ExistsFilterOperator } from "@redotech/redo-model/views/advanced-filters/exists-filter";
import { AdvancedFilterType } from "@redotech/redo-model/views/advanced-filters/generic-advanced-filter-data";
import { AdvancedFilterData } from "@redotech/redo-model/views/views";
import { Card } from "@redotech/redo-web/card";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { SkeletonText } from "@redotech/redo-web/skeleton";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import * as styles from "./pick-sessions.module.css";

export const PickSessionMetrics = memo(function PickSessionMetrics() {
  const { t } = useTranslation();
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const team = useRequiredContext(TeamContext);

  const slaDateCountsLoad = useLoad(() => {
    return rpcClient.getSlaDateCounts({
      merchantTimezone: team.ianaTimezone,
      locationId: null,
    });
  }, [rpcClient, team]);

  const openOrders = useLoad(async () => {
    const advancedFiltersData: AdvancedFilterData[] = [
      {
        name: "status",
        type: AdvancedFilterType.ARRAY_TO_STRING,
        value: [FulfillmentGroupStatus.Open],
        operator: ArrayStringFilterOperator.ANY_OF,
      },
      {
        name: "pickSessionId",
        type: AdvancedFilterType.EXISTS,
        value: null,
        operator: ExistsFilterOperator.NOT_EXISTS,
      },
    ];

    return (
      await rpcClient.getFulfillmentGroupSummaryCount({
        advancedFiltersData,
        merchantTimezone: team.ianaTimezone,
      })
    ).total;
  }, [team, rpcClient]);

  const orderFulfilledToday = useLoad(async () => {
    const advancedFiltersData: AdvancedFilterData[] = [
      {
        name: "labelPurchasedDate",
        type: AdvancedFilterType.DATE,
        value: KnownDateFilterTimeFrame.TODAY,
        operator: DateFilterOperator.WITHIN,
      },
    ];

    return (
      await rpcClient.getFulfillmentGroupSummaryCount({
        advancedFiltersData,
        merchantTimezone: team.ianaTimezone,
      })
    ).total;
  }, [team, rpcClient]);

  return (
    <Flex className={styles.metricCardsContainer} gap="3xl">
      <PickMetricCard
        pending={openOrders?.pending}
        title={t("Unfulfilled orders")}
        value={openOrders?.value}
      />
      <PickMetricCard
        pending={slaDateCountsLoad.pending}
        title={t("Orders due today")}
        value={slaDateCountsLoad.value?.dueToday}
      />
      <PickMetricCard
        pending={orderFulfilledToday?.pending}
        title={t("Orders fulfilled today")}
        value={orderFulfilledToday?.value}
      />
    </Flex>
  );
});

const PickMetricCard = memo(function PickMetricCard({
  title,
  value,
  pending,
}: {
  title: string;
  value: number | undefined;
  pending: boolean;
}) {
  return (
    <Card borderColor="primary" p="xl" radius="md" w="full">
      <Flex dir="column" gap="sm">
        <Flex align="center" justify="space-between">
          <Text fontSize="xs" fontWeight="medium" textColor="tertiary">
            {title}
          </Text>
        </Flex>
        <Text fontSize="md" fontWeight="bold" textColor="primary">
          {pending ? <SkeletonText length={3} /> : value?.toLocaleString()}
        </Text>
      </Flex>
    </Card>
  );
});
